// src/pages/NewsletterUnsubscribe.tsx
import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Header from '../components/Header';

export default function NewsletterUnsubscribe() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');

  const BASE = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1`;
  const FN_NAMES = ['newsletter-unsubscribe'];

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage('Lien invalide (token manquant).');
      return;
    }

    const run = async () => {
      try {
        const encoded = encodeURIComponent(token);

        for (const fn of FN_NAMES) {
          const url = `${BASE}/${fn}?token=${encoded}`;

          const res = await fetch(url, {
            method: 'GET',
            cache: 'no-store',
            headers: {
              Accept: 'application/json',
            },
          });

          // si function introuvable -> on essaie l’autre
          if (res.status === 404) {
            const t = await res.text().catch(() => '');
            console.warn('Function not found:', fn, res.status, t);
            continue;
          }

          const ct = res.headers.get('content-type') || '';
          const payload: any = ct.includes('application/json')
            ? await res.json().catch(() => ({}))
            : {};

          if (res.ok) {
            const msg = (payload?.message || '').toString().toLowerCase();
            const state = (payload?.state || '').toString().toLowerCase();

            if (payload?.ok === false) {
              setStatus('error');
              setMessage(payload?.message || "Ce lien semble invalide ou a déjà été utilisé.");
              return;
            }

            if (msg.includes('invalide') || state === 'invalid') {
              setStatus('error');
              setMessage("Ce lien semble invalide ou a déjà été utilisé.");
              return;
            }

            // Déjà désabonné : on affiche un succès propre pour éviter de frustrer l'utilisateur.
            setStatus('success');
            setMessage(payload?.message || 'Votre adresse a bien été retirée de notre liste d’envoi.');
            return;
          }

          const errText = payload?.message || payload?.error || await res.text().catch(() => '');
          console.error('Unsubscribe failed:', res.status, errText);
          setStatus('error');
          setMessage(errText || "Ce lien semble invalide ou a déjà été utilisé.");
          return;
        }

        // Les 2 fonctions n’existent pas (ne devrait plus arriver chez toi)
        setStatus('error');
        setMessage('Service de désabonnement introuvable (Edge Function).');
      } catch (e) {
        console.error(e);
        setStatus('error');
        setMessage('Une erreur technique est survenue.');
      }
    };

    run();
  }, [token, BASE]);

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <div className="container mx-auto px-4 pt-32 flex justify-center">
        <div className="bg-white p-8 rounded-2xl shadow-sm border border-slate-200 max-w-md w-full text-center">
          {status === 'loading' && (
            <div className="py-10">
              <div className="animate-spin w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="font-bold text-slate-500">Désabonnement en cours...</p>
            </div>
          )}

          {status === 'success' && (
            <div className="py-6">
              <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-6 text-2xl">
                ✅
              </div>
              <h1 className="text-2xl font-black text-slate-900 mb-2">Vous êtes désabonné(e)</h1>
              <p className="text-slate-600 mb-8">
                {message || 'Votre adresse a bien été retirée de notre liste d’envoi.'}
              </p>
              <Link to="/" className="bg-slate-900 text-white px-6 py-3 rounded-xl font-bold hover:bg-slate-800 transition">
                Retour à l&apos;accueil
              </Link>
            </div>
          )}

          {status === 'error' && (
            <div className="py-6">
              <div className="w-16 h-16 bg-red-100 text-red-600 rounded-full flex items-center justify-center mx-auto mb-6 text-2xl">
                ⚠️
              </div>
              <h1 className="text-2xl font-black text-slate-900 mb-2">Oups !</h1>
              <p className="text-slate-600 mb-8">{message}</p>
              <Link to="/" className="text-slate-500 underline font-bold hover:text-slate-800">
                Retour au site
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
